import * as React from 'react';
import { cn } from '@/lib/utils';

export interface RadioOption {
  value: string;
  label: React.ReactNode;
  /** Greyed out and not selectable; `hint` explains why. */
  disabled?: boolean;
  hint?: React.ReactNode;
}

export interface RadioGroupProps {
  /** Accessible name for the group (the visible label's text). */
  name: string;
  value: string;
  onChange: (value: string) => void;
  options: RadioOption[];
  className?: string;
}

/**
 * RadioGroup — shared single-choice control (POS-I237). Native radios styled with semantic
 * tokens, logical spacing (RTL safe), a visible hint line for a disabled option.
 */
export function RadioGroup({ name, value, onChange, options, className }: RadioGroupProps) {
  const id = React.useId();
  return (
    <div role="radiogroup" aria-label={name} className={cn('space-y-2', className)}>
      {options.map((opt) => (
        <label
          key={opt.value}
          className={cn(
            'flex items-start gap-3 rounded-md border border-input px-3 py-2 text-sm',
            opt.disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-muted/50',
            value === opt.value && !opt.disabled && 'border-primary bg-primary/5',
          )}
        >
          <input
            type="radio"
            name={`${id}-${name}`}
            value={opt.value}
            checked={value === opt.value}
            disabled={opt.disabled}
            onChange={() => onChange(opt.value)}
            className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
          />
          <span className="min-w-0">
            <span className="block">{opt.label}</span>
            {opt.disabled && opt.hint && (
              <span className="block text-xs text-muted-foreground">{opt.hint}</span>
            )}
          </span>
        </label>
      ))}
    </div>
  );
}
